import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RULES_VERSION } from '@il-colpo/engine';
import { formatTag } from '@il-colpo/shared/account';
import { useGameStore } from '../../game/gameStore.ts';
import { useFlag } from '../../meta/liveStore.ts';
import { useMetaClient, useRemote } from '../../meta/hooks.ts';
import type { ReplayData } from '../../meta/types.ts';
import { ReplayClient } from '../../replay/replayClient.ts';
import { Button, Panel, ScreenFrame } from '../../ui/index.ts';
import { GatePanel, Page, useMetaGate } from './common.tsx';
import styles from './Meta.module.css';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** The names to show: who sat at each seat, and a name for the bots, which the log does not keep. */
function namesOf(data: ReplayData, bot: (seat: string) => string): Record<string, string> {
  const names: Record<string, string> = {};
  for (const p of data.log.config.players) names[p.id] = bot(p.id);
  for (const r of data.roster) {
    if (r.nickname !== null && r.tag !== null) names[r.seat] = `${r.nickname}#${formatTag(r.tag)}`;
  }
  return names;
}

/**
 * `/replay/:id`: a heist the player sat at, watched again. It loads the log, rebuilds the heist with
 * the engine and hands it to the game screens (`ReplayClient`), which show it from the player's seat.
 */
export function ReplayScreen() {
  const { t } = useTranslation('meta');
  const { id = '' } = useParams();
  // From a friend's page: the seat the friend had, and where to go back to.
  const [params] = useSearchParams();
  const wantedSeat = params.get('seat');
  const backTo = params.get('from') === 'friends' ? '/friends' : '/progress';
  const navigate = useNavigate();
  const gate = useMetaGate();
  const enabled = useFlag('replays');
  const meta = useMetaClient();
  const valid = UUID.test(id);
  const load = useMemo(
    () => (meta && gate === 'ready' && enabled && valid ? () => meta.replay(id) : null),
    [meta, gate, enabled, valid, id],
  );
  const replay = useRemote<ReplayData>(load);
  const data = replay.data;
  const tooOld = data !== null && data.log.rulesVersion !== RULES_VERSION;

  useEffect(() => {
    if (!data || tooOld) return;
    const mine =
      data.roster.find((r) => r.isMe)?.seat ??
      data.roster.find((r) => r.seat === wantedSeat)?.seat ??
      data.roster[0]?.seat ??
      'p1';
    const client = new ReplayClient(data.log, {
      seat: mine,
      names: namesOf(data, (seat) => t('replay.bot', { seat })),
    });
    useGameStore.getState().attach(client, backTo);
    void navigate('/game', { replace: true });
  }, [data, tooOld, navigate, t, wantedSeat, backTo]);

  return (
    <ScreenFrame title={t('replay.title')} backTo={backTo}>
      <Page>
        {gate !== 'ready' ? <GatePanel gate={gate} /> : null}
        {gate === 'ready' && !enabled ? (
          <Panel>
            <p className={styles['note']}>{t('replay.off')}</p>
          </Panel>
        ) : null}
        {gate === 'ready' && enabled && !valid ? (
          <Panel>
            <p className={styles['error']} role="alert">
              {t('replay.notFound')}
            </p>
          </Panel>
        ) : null}
        {replay.status === 'error' ? (
          <Panel>
            <div className={styles['stack']}>
              <p className={styles['error']} role="alert">
                {t('replay.notFound')}
              </p>
              <div>
                <Button variant="secondary" onClick={replay.reload}>
                  {t('gate.retry')}
                </Button>
              </div>
            </div>
          </Panel>
        ) : null}
        {tooOld && data ? (
          <Panel>
            <p className={styles['error']} role="alert">
              {t('replay.oldRules', { version: data.log.rulesVersion })}
            </p>
          </Panel>
        ) : null}
        {gate === 'ready' && enabled && valid && replay.status === 'loading' ? (
          <GatePanel gate="loading" />
        ) : null}
      </Page>
    </ScreenFrame>
  );
}
